<template>
  <div id="accordionTemplateContacts" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingTemplateContacts" class="accordion-header">
        <button
          aria-controls="collapseTemplateContacts"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseTemplateContacts"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex">
            <div class="bg-secondary rounded-xs h-fit-content me-8">
              <v-icon icon="contact" size="xxl" space="p-12" />
            </div>

            <div>
              <div class="h4 ff-secondary mb-0">{{ $t("template.contact.contacts") }}</div>
              <div class="fw-light accordion-subtitle">{{ $t("template.contact.contactsSubtitle") }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseTemplateContacts"
        aria-labelledby="headingTemplateContacts"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionTemplateContacts"
      >
        <div class="accordion-body pb-32 pb-xl-48">
          <form id="template-update-contact-text-form" class="d-flex flex-column justify-content-between h-100">
            <fieldset>
              <legend class="visually-hidden">{{ $t("template.contact.contactInformation") }}</legend>

              <div class="form-outline">
                <label class="label">
                  <span>{{ $t("template.contact.contactInformation") }}</span>
                </label>

                <v-html-editor
                  id="template-update-contact-text"
                  ref="template-update-contact-text-ref"
                  :value="contactInformationLocal"
                  @input="contactInformationLocal = $event"
                  @media-uploaded="editorMedia.push($event)"
                />
              </div>
            </fieldset>

            <v-button
              :disabled="!meta.valid || disableButton || !meta.dirty"
              :is-loading="accepted"
              class="btn-icon w-fit align-self-end mt-24"
              size="sm"
              variant="primary"
              @click="updateTemplate"
            >
              {{ $t("general.button.save") }}
              <v-icon icon="arrow-right" size="sm" space="ms-12" />
            </v-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import * as yup from "yup";
import { useField } from "vee-validate";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VHtmlEditor from "@/components/vendor/htmlEditor/VHtmlEditor.vue";
import api from "@/api";
import useFormValidation from "@/helpers/form";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import Template from "@/api/objects/Template";
import { useToast } from "vue-toastification";

const props = defineProps({
  agency: {
    type: String,
    required: true,
  },
  template: {
    type: Object as PropType<Template>,
    required: true,
  },
});

const emit = defineEmits(["reloadTemplate"]);
const { t } = useI18n();
const toast = useToast();

const rules = yup.object({
  contactInformationLocal: yup.string().nullable().label(t("template.contact.contactInformation")),
});
const initialValues = {
  contactInformationLocal: props.template.contactText,
};
const form = useFormValidation(rules, initialValues);
const { value: contactInformationLocal } = useField<string>("contactInformationLocal");
const { meta, accepted, toggleAccepted } = form;

const disableButton = ref(false);
const editorMedia = ref([]);

const updateTemplate = async () => {
  try {
    toggleAccepted();
    disableButton.value = true;

    const data = {
      name: props.template.name,
      travel_type: props.template.travelType,
      origin: props.template.originId,
      destination: props.template.destinationId,
      is_published: props.template.isPublished,
      terms_conditions_text: props.template.termsConditionsText,
      complementary_text: props.template.complementaryText,
      contact_text: contactInformationLocal.value,
    };

    const template = await api.activityService.updateActivity(props.agency, props.template.id, data);

    if (editorMedia.value.length > 0) {
      const mediaData = {
        media: editorMedia.value,
        model_type: "activity",
        collection: "tinymce",
        model_id: props.template.id,
      };

      await api.media.confirm("s3", mediaData);
    }

    toast.success("Saved successfully", { position: "bottom-right" });

    emit("reloadTemplate");

    disableButton.value = true;
    toggleAccepted();
  } catch (e: any) {
    disableButton.value = false;
    toggleAccepted();

    console.error(e);
    toast.error(e.response?.data?.message || "An error occurred", { position: "bottom-right" });
  }
};
</script>
